import Blocklist from '../blocklist/Blocklist'
import { Device } from '../helpers'
import { domReady } from '../helpers/DOM'
import Logger from '../helpers/Logger'
import type WebModel from '../models/WebModel'
import { OUTSTREAM_REFRESH_TIME } from '../outstream/OutstreamRenderer'
import type PrebidFacade from '../prebidFacade/PrebidFacade'
import { UniversalPlayer } from '../slots/UniversalPlayer'
import type { Slot } from '../slots/Slot'
import type SlotFactory from '../slots/SlotFactory'
import {
  VIDEO_PLAYER_CLICKED_EVENT,
  VIDEO_TARGET_CLASS
} from '../stickyVideo/StickyVideo'

const INIT_UVP_EVENT_NAME = 'mv-init-uvp'
/**
 * UniversalPlayers class
 * Responsible for displaying UniversalPlayer slots
 */
export class UniversalPlayers {
  createdSlot: UniversalPlayer | undefined
  renderedPromise: Promise<void>

  constructor(
    readonly webModel: WebModel,
    readonly slotFactory: SlotFactory,
    readonly blocklist: Pick<Blocklist, 'contains'>,
    readonly prebidFacade: PrebidFacade
  ) {
    // Close the UvP if a VideoPlayer is clicked.
    window.addEventListener(VIDEO_PLAYER_CLICKED_EVENT, this.handleCTPClick)

    // Skip if Universal Players are not turned on for this device
    if (!this.shouldDisplayUniversalPlayer()) {
      this.renderedPromise = Promise.resolve()
      this.setupUvpListener()
      return
    }
    this.renderedPromise = new Promise((resolve) => {
      domReady(() => {
        this.initUniversalPlayer().then(() => {
          resolve()
        })
      })
    })
  }

  /**
   * Initializes a new Universal Player, if one doesn't already exist.
   * If a new UniversalPlayer is created, it returns the new slot; if one already existed, it returns undefined.
   */
  async initUniversalPlayer() {
    // Don't need to create a new UvP if one is already open.
    if (this.createdSlot && !this.createdSlot.hasBeenClosed) {
      return
    }
    const slot = await this.createAndDisplaySlot(this.slotFactory)

    return slot
  }

  /**
   * If we're NOT setting up Universal Player on load, add a listener to handle the 'mv-init-uvp' event
   * that the instream video player will emit if it doesn't get a bid, or something else goes wrong.
   */
  async setupUvpListener() {
    // 'mv-init-uvp' gets emitted by the video player when it wants the wrapper to start the UniversalPlayer.
    // This happens whenever the player reverts to Click-to-Play from Autoplay.
    // E.g.: when there are no bids; when the playlist finishes, etc.
    window.addEventListener(INIT_UVP_EVENT_NAME, async () => {
      const slot = await this.initUniversalPlayer()
      if (slot) {
        // UniversalPlayer isn't a reRenderable slot, so we need to start the bidding manually here.
        this.prebidFacade.requestBids([slot], slot.timeout.value)
      }
    })
  }

  shouldDisplayUniversalPlayer(): boolean {
    const hasAutoplayMvVideo = !!document.querySelector(
      `.${VIDEO_TARGET_CLASS}[data-autoplay=true],.${VIDEO_TARGET_CLASS}[data-automobile=true]`
    )
    const isDesktop = !Device.isMobileOrTablet
    const isMobileEnabled =
      !isDesktop &&
      !!this.webModel.universal_player_mobile &&
      !this.blocklist.contains('universal_player_mobile')
    const isDesktopEnabled =
      isDesktop &&
      !!this.webModel.universal_player_desktop &&
      !this.blocklist.contains('universal_player_desktop')

    return (isDesktopEnabled || isMobileEnabled) && !hasAutoplayMvVideo
  }

  // We only want to run the handleCTPClick function once!
  handleCTPClick = () => {
    this.createdSlot?.onClose()
  }

  setupUniversalPlayerSlot(): ISlotModel {
    const slotModel: ISlotModel = {
      adUnitId: 'universalPlayer_atf',
      type: 'UniversalPlayer' as SlotClass,
      selector: 'body',
      insertPosition: 'beforeend',
      refreshTime: OUTSTREAM_REFRESH_TIME
    }
    return slotModel
  }

  // This function actually sets up the UniversalPlayer slot on the page.
  // Called in Atf.ts.
  async createAndDisplaySlot(
    slotFactory: SlotFactory
  ): Promise<Slot | undefined> {
    try {
      const slot = (await slotFactory.new(
        this.setupUniversalPlayerSlot()
      )) as UniversalPlayer
      this.createdSlot = slot
      return slot
    } catch (e) {
      Logger.debug(e)
    }
  }
}
